import React, { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { makePdf } from "./Reports";
import { istDateTime } from "../components/AnnaTicketSlip";
import { ANNA_TYPES, ANNA_CATS } from "./AnnaIssue";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FileDown, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const istDay = (iso) => new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
const todayIst = () => istDay(new Date().toISOString());
const dmy = (d) => d.split("-").reverse().join("/");
const PAGE = 50;
const sel = "h-10 rounded-md border border-input bg-white px-3 text-sm";

// First and last ticket number in the rows (rows come sorted by issue time).
const span = (rs) => (rs.length ? [rs[0].ticket_no, rs[rs.length - 1].ticket_no] : ["-", "-"]);

function count(rows) {
  const o = { total: rows.length, verified: rows.filter((r) => r.status === "VERIFIED").length };
  ANNA_CATS.forEach((c) => { o[c.key] = rows.filter((r) => r.category === c.key).length; });
  return o;
}

function Table({ head, rows, foot, testid }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead><tr className="bg-maroon-800 text-white">{head.map((h) => <th key={h} className="px-2 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={head.length} className="text-center text-slate-500 py-6">No tickets for the selected filters</td></tr>}
          {rows.map((r, i) => <tr key={i} className="border-b border-[#F0E6D2] hover:bg-gold-50/50">{r.map((c, j) => <td key={j} className="px-2 py-1.5 whitespace-nowrap">{c}</td>)}</tr>)}
        </tbody>
        {foot && rows.length > 0 && <tfoot><tr className="bg-gold-50 font-bold text-maroon-900">{foot.map((c, j) => <td key={j} className="px-2 py-2">{c}</td>)}</tr></tfoot>}
      </table>
    </div>
  );
}

function Paged({ head, rows, testid }) {
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [rows]);
  const pages = Math.max(1, Math.ceil(rows.length / PAGE));
  return (
    <>
      <Table head={head} rows={rows.slice((page - 1) * PAGE, page * PAGE)} testid={testid} />
      {pages > 1 && (
        <div className="flex items-center justify-center gap-1 mt-3 flex-wrap">
          <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage(page - 1)}>Prev</Button>
          {Array.from({ length: pages }, (_, i) => i + 1).filter((p) => p === 1 || p === pages || Math.abs(p - page) <= 2).map((p, i, arr) => (
            <React.Fragment key={p}>
              {i > 0 && p - arr[i - 1] > 1 && <span className="px-1 text-slate-400">…</span>}
              <Button size="sm" variant={p === page ? "default" : "outline"} className={p === page ? "bg-maroon-700 text-white" : ""} onClick={() => setPage(p)}>{p}</Button>
            </React.Fragment>
          ))}
          <Button size="sm" variant="outline" disabled={page === pages} onClick={() => setPage(page + 1)}>Next</Button>
          <span className="text-xs text-slate-500 ml-2">{rows.length} tickets</span>
        </div>
      )}
    </>
  );
}

export default function AnnaReports() {
  const { user } = useAuth();
  const [temples, setTemples] = useState([]);
  const [f, setF] = useState({ frm: todayIst(), to: todayIst(), ticket_type: "", category: "", temple_id: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("abstract");

  useEffect(() => { api.get("/meta/form-data").then((r) => setTemples(r.data.temples || [])).catch(() => {}); }, []);
  const run = () => {
    setLoading(true);
    const q = Object.fromEntries(Object.entries(f).filter(([, v]) => v));
    api.get("/anna/reports", { params: q }).then((r) => setRows(r.data.rows)).catch((e) => toast.error(fmtErr(e))).finally(() => setLoading(false));
  };
  useEffect(run, []); // eslint-disable-line

  const temple = temples.find((t) => t.temple_id === (f.temple_id || user.temple_id)) || (temples.length === 1 ? temples[0] : null);
  const typeLabel = (k) => ANNA_TYPES.find((x) => x.key === k)?.label || k;
  const catHead = ANNA_CATS.map((c) => c.label);
  const period = f.frm === f.to ? dmy(f.frm) : `${dmy(f.frm || "")} to ${dmy(f.to || "")}`;

  const abstract = useMemo(() => {
    const body = ANNA_TYPES.map((t) => { const rs = rows.filter((r) => r.ticket_type === t.key); const c = count(rs); return [typeLabel(t.key), ...span(rs), ...ANNA_CATS.map((x) => c[x.key]), c.total, c.verified, c.total - c.verified]; });
    const c = count(rows);
    return { body, foot: ["Grand Total", ...span(rows), ...ANNA_CATS.map((x) => c[x.key]), c.total, c.verified, c.total - c.verified] };
  }, [rows]); // eslint-disable-line
  const daywise = useMemo(() => {
    const keys = [...new Set(rows.map((r) => `${istDay(r.created_at)}|${r.ticket_type}`))].sort();
    const body = keys.map((k) => { const [d, t] = k.split("|"); const rs = rows.filter((r) => istDay(r.created_at) === d && r.ticket_type === t); const c = count(rs); return [dmy(d), typeLabel(t), ...span(rs), ...ANNA_CATS.map((x) => c[x.key]), c.total, c.verified]; });
    const c = count(rows);
    return { body, foot: ["Total", "", ...span(rows), ...ANNA_CATS.map((x) => c[x.key]), c.total, c.verified] };
  }, [rows]); // eslint-disable-line
  const category = useMemo(() => {
    const c = count(rows);
    const body = ANNA_CATS.map((x) => { const sub = rows.filter((r) => r.category === x.key); return [x.label, sub.length, sub.filter((r) => r.status === "VERIFIED").length, c.total ? `${((sub.length / c.total) * 100).toFixed(1)}%` : "0%"]; });
    return { body, foot: ["Total", c.total, c.verified, c.total ? "100%" : "0%"] };
  }, [rows]);
  const detail = useMemo(() => rows.map((r, i) => [i + 1, r.ticket_no, typeLabel(r.ticket_type), r.category_name, istDateTime(r.created_at), r.counter_name || "-", r.shift_name || "-", r.issued_by, r.status === "VERIFIED" ? "Verified" : "Issued", r.verified_at ? istDateTime(r.verified_at) : "-", r.verified_by || "-"]), [rows]); // eslint-disable-line
  const verified = useMemo(() => rows.filter((r) => r.status === "VERIFIED").map((r, i) => [i + 1, r.ticket_no, typeLabel(r.ticket_type), r.category_name, istDateTime(r.created_at), istDateTime(r.verified_at), r.verified_by || "-", r.verified_counter_name || "-",
    `${Math.max(0, Math.round((new Date(r.verified_at) - new Date(r.created_at)) / 60000))} min`]), [rows]); // eslint-disable-line

  const H = {
    abstract: ["Ticket Type", "Start Ticket No", "End Ticket No", ...catHead, "Total", "Verified", "Not Verified"],
    daywise: ["Date", "Ticket Type", "Start Ticket No", "End Ticket No", ...catHead, "Total", "Verified"],
    category: ["Category", "Tickets", "Verified", "Share"],
    detail: ["S.No", "Ticket No", "Type", "Category", "Issued", "Counter", "Shift", "Issued By", "Status", "Verified At", "Verified By"],
    verification: ["S.No", "Ticket No", "Type", "Category", "Issued", "Verified At", "Verified By", "Verify Gate", "Wait"],
  };
  const TITLES = { abstract: "Abstract", daywise: "Day-wise Abstract", category: "Category Report", detail: "Detail Report", verification: "Verification Report" };

  const pdf = () => {
    const data = { abstract, daywise, category, detail: { body: detail }, verification: { body: verified } }[tab];
    if (!data.body.length || !rows.length) { toast.error("No data to export"); return; }
    const filt = [f.ticket_type && typeLabel(f.ticket_type), f.category && ANNA_CATS.find((c) => c.key === f.category)?.label].filter(Boolean).join(", ");
    makePdf({
      temple, title: `Annaprasadam ${TITLES[tab]} - ${period}${filt ? ` (${filt})` : ""}`,
      head: H[tab], body: data.body, foot: data.foot, landscape: ["detail", "abstract", "daywise"].includes(tab),
      filename: `Annaprasadam_${TITLES[tab].replace(/\W+/g, "_")}_${f.frm || "all"}.pdf`,
    });
  };

  return (
    <div data-testid="anna-reports-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-bold text-maroon-900 mb-1">Annaprasadam Reports</h1>
      <p className="text-sm text-slate-500 mb-4">{temple ? temple.name : "All temples"} · {period}</p>
      <Card className="mb-4">
        <CardContent className="p-4 grid grid-cols-2 md:grid-cols-6 gap-3 items-end">
          <div><Label>From</Label><Input type="date" value={f.frm} onChange={(e) => setF({ ...f, frm: e.target.value })} data-testid="anna-rep-from" /></div>
          <div><Label>To</Label><Input type="date" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} data-testid="anna-rep-to" /></div>
          <div><Label>Ticket Type</Label>
            <select className={`${sel} w-full`} value={f.ticket_type} onChange={(e) => setF({ ...f, ticket_type: e.target.value })}>
              <option value="">All</option>{ANNA_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select></div>
          <div><Label>Category</Label>
            <select className={`${sel} w-full`} value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              <option value="">All</option>{ANNA_CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select></div>
          {user.role === "SUPER_ADMIN" ? (
            <div><Label>Temple</Label>
              <select className={`${sel} w-full`} value={f.temple_id} onChange={(e) => setF({ ...f, temple_id: e.target.value })}>
                <option value="">All temples</option>{temples.map((t) => <option key={t.temple_id} value={t.temple_id}>{t.temple_id} · {t.name}</option>)}
              </select></div>
          ) : <div className="hidden md:block" />}
          <div className="flex gap-2 col-span-2 md:col-span-1">
            <Button onClick={run} disabled={loading} className="flex-1 bg-maroon-700 hover:bg-maroon-800 text-white" data-testid="anna-rep-run"><RefreshCw className="w-4 h-4 mr-1" /> Generate</Button>
            <Button onClick={pdf} variant="outline" className="flex-1" data-testid="anna-rep-pdf"><FileDown className="w-4 h-4 mr-1" /> PDF</Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-white border border-[#E5D9C5] flex-wrap h-auto">
          {Object.entries(TITLES).map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`anna-tab-${k}`}>{v}</TabsTrigger>)}
        </TabsList>
        <Card className="mt-3"><CardContent className="p-3">
          <TabsContent value="abstract" className="mt-0"><Table head={H.abstract} rows={abstract.body} foot={abstract.foot} testid="anna-abstract" /></TabsContent>
          <TabsContent value="daywise" className="mt-0"><Table head={H.daywise} rows={daywise.body} foot={daywise.foot} /></TabsContent>
          <TabsContent value="category" className="mt-0"><Table head={H.category} rows={category.body} foot={category.foot} /></TabsContent>
          <TabsContent value="detail" className="mt-0"><Paged head={H.detail} rows={detail} testid="anna-detail" /></TabsContent>
          <TabsContent value="verification" className="mt-0"><Paged head={H.verification} rows={verified} /></TabsContent>
        </CardContent></Card>
      </Tabs>
    </div>
  );
}
